import { useScript } from "@channelwill/hooks"
import { createCrossSellTracker } from "@packages/cross-sell-tracking"
import { Redirect } from "@shopify/app-bridge/actions"
import { Action as RedirectAction } from "@shopify/app-bridge/actions/Navigation/Redirect"
import { isMobile, isShopifyEmbedded } from "@shopify/app-bridge-utils"
import { FooterHelp, Tabs } from "@shopify/polaris"
import { Sentry } from "channelwill-sentry-sdk"
import { get, startsWith } from "lodash-es"
import { Observer } from "mobx-react"
import React, { Suspense, useEffect, useLayoutEffect, useRef } from "react"
import { getI18n, useTranslation } from "react-i18next"
import { APPEnum, getDevToken } from "react-pp-bundle"
import { NavigateFunction } from "react-router"
import { Navigate, Outlet, Route, Routes, useLocation, useNavigate, useSearchParams } from "react-router-dom"
import { IntercomProps, useIntercom } from "react-use-intercom"

import { postRouteMessage } from "@/admin-bridge/admin-bridge"
import { AppPricingInterval } from "@/api/account/type.d"
import { APICode } from "@/api/APICode"
import appApi from "@/api/app"
import dashboard from "@/api/dashboard"
import trackingPageAPI from "@/api/tracking-page"
import { FeedbackModal, Footer, FullScreenLoading } from "@/component"
import { CWComponentsProvider } from "@/component/CWComponents"
import DevCustomerAuthGateSwitch from "@/component/DevCustomerAuthGateSwitch"
import { PostTemplate } from "@/component/PostLayer/PostTemplate.d"
import ShopifyAppNav from "@/component/ShopifyAppNav"
import { SkeletonGeneral, SkeletonNotifications, SkeletonPrePurchaseEDD } from "@/component/SkeletonSettings"
import SkeletonTrackingPageAppearance from "@/component/SkeletonTrackingPage/SkeletonTrackingPage"
// DeliveryGuarantee保险app页面
import env from "@/config/env"
import { CROSS_SELL_APP_KEY } from "@/enums/CrossSellPoint"
import { differenceLangMap } from "@/i18n/resource"
import { Data as UserInfoResponseData, IUserInfo } from "@/models/IUserInfo"
import { Account, OrdersUpdateStatus } from "@/pages"
// Analytics统计
import Analytics from "@/pages/Analytics"
import SkeletonAnalytics from "@/pages/Analytics/SkeletonAnalytics"
import RecommendPage from "@/pages/Dashboard/recommend"
import SkeletonDelivery from "@/pages/DeliveryGuarantee/components/SkeletonDelivery"
import SkeletonTable from "@/pages/DeliveryGuarantee/components/SkeletonTable"
import DeliveryGuarantee from "@/pages/DeliveryGuarantee/DeliveryGuarantee"
import CarrierMatching from "@/pages/Orders/CarrierMatching"
import { ShipmentDrawer } from "@/pages/Orders/components/ShipmentDrawer/ShipmentDrawer"
import SkeletonOrders from "@/pages/Orders/components/SkeletonOrders/SkeletonOrders"
import OrdersDetail from "@/pages/Orders/OrdersDetail"
// Settings
import Settings from "@/pages/Settings"
import SkeletonTrackingPage from "@/pages/TrackingPage/SkeletonTrackingPage/SkeletonTrackingPage"
import CustomizeBackdropPage from "@/pages/TrackingPage/ThemeLayoutCustomize/CustomizeBackdropPage"
import CustomizeShowcasePage from "@/pages/TrackingPage/ThemeLayoutCustomize/CustomizeShowcasePage"
import TrackingPage from "@/pages/TrackingPage/TrackingPage"
import { orderPost, pathnameHandle, setCurrentShopifyAppPath } from "@/router/history"
import stores from "@/stores"
import AppBridge, { getAppBase, getAppLink } from "@/utils/appBridge"
import { setBetaFeatures } from "@/utils/BetaFeature"
import { ADMIN_SCOPED_FALLBACK_PATH, isAdminScopedPath } from "@/utils/customerAuthGate"
import { getCWReportBaseOptions, submitAdProperties } from "@/utils/cwAdTracking"
import { setDefaultTimezone, setLocale } from "@/utils/datetime"
import { eventBus } from "@/utils/EventBus"
import { EventType } from "@/utils/events"
import { getIntercomBootProps } from "@/utils/intercom"
import { isFromAdmin } from "@/utils/is-from-admin"
import { LanguageUtils } from "@/utils/languages"
import { IListenerNameEnum } from "@/utils/languages.d"
import { getShopifyIdToken } from "@/utils/shopifySessionToken"
import toast from "@/utils/toast"
import TokenHelper from "@/utils/tokenHelper"
import UrlUtils from "@/utils/UrlUtils"

import DynamicLoadError from "./component/DynamicLoadError/DynamicLoadError"
import PostLayer from "./component/PostLayer/PostLayer"
import SkeletonIntegration from "./component/SkeletonIntegration/SkeletonIntegration"
import AddOns from "./pages/AddOns/index"
import Dashboard from "./pages/Dashboard/index"

const Orders = React.lazy(() => import("@/pages/Orders/Orders"))
const UpgradeRequired = React.lazy(() => import("@/component/UpgradeRequired/UpgradeRequired"))

const Integration = React.lazy(() => import("@/pages/Integration/Integration"))
const IntegrationEvents = React.lazy(() => import("@/pages/Integration/Events/Events"))

// const OldSettings = React.lazy(() => import("@/pages/Settings/Settings"))
const General = React.lazy(() => import("@/pages/Settings/General"))
const ShippingNotifications = React.lazy(() => import("@/pages/Settings/ShippingNotifications"))
const PrePurchaseEDD = React.lazy(() => import("@/pages/Settings/PrePurchaseEDD"))
const SystemNotifications = React.lazy(() => import("@/pages/Settings/SystemNotifications"))
const EmailEditor = React.lazy(() => import("@/pages/Settings/EmailEdit/EmailEditPage"))

const Billing = React.lazy(() => import("@/pages/Billing"))

// Tracking Page
const DisplayOptions = React.lazy(() => import("@/pages/TrackingPage/DisplayOptions/DisplayOptions"))
const TrackingPageTranslations = React.lazy(() => import("@/pages/TrackingPage/TrackingPageTranslations/TrackingPageTranslations"))
const CustomOrderStatus = React.lazy(() => import("@/pages/TrackingPage/CustomOrderStatus/CustomOrderStatus"))
const EstimatedDeliveryTime = React.lazy(() => import("@/pages/TrackingPage/EstimatedDeliveryTime/EstimatedDeliveryTime"))
const ProductRecommendationUpsell = React.lazy(() => import("@/pages/TrackingPage/ProductRecommendationUpsell/ProductRecommendationUpsell"))
const SEOOptimization = React.lazy(() => import("@/pages/TrackingPage/SEOOptimization/SEOOptimization"))
const TrackingPageURL = React.lazy(() => import("@/pages/TrackingPage/TrackingPageURL/TrackingPageURL"))
const CustomCSSAndHTML = React.lazy(() => import("@/pages/TrackingPage/CustomCSSAndHTML/CustomCSSAndHTML"))

const AnalyticsShipment = React.lazy(() => import("@/pages/Analytics/shipment"))
const AnalyticsDeliveryTime = React.lazy(() => import("@/pages/Analytics/delivery-time"))
const AnalyticsTransitTime = React.lazy(() => import("@/pages/Analytics/transitTime"))
const AnalyticsExceptions = React.lazy(() => import("@/pages/Analytics/exceptions"))
const AnalyticsTrackingPage = React.lazy(() => import("@/pages/Analytics/trackingPage"))
const AnalyticsShippingNotifications = React.lazy(() => import("@/pages/Analytics/shippingNotifications"))

const DGSetting = React.lazy(() => import("@/pages/DeliveryGuarantee/DGSetting/DGSetting"))
const Covers = React.lazy(() => import("@/pages/DeliveryGuarantee/Covers/Covers"))
const Claims = React.lazy(() => import("@/pages/DeliveryGuarantee/Claims/Claims"))
const Transactions = React.lazy(() => import("@/pages/DeliveryGuarantee/Transactions/Transactions"))

const App = () => {
  const { boot, shutdown } = useIntercom()
  const { pathname, search } = useLocation()

  const navigate = useNavigate()
  const { t } = useTranslation("common")

  useEffect(() => {
    /* 处理 dayjs 多语言 */
    const changeDayjsLocale = ({ locale }: { locale: string }) => {
      setLocale(locale)
    }

    eventBus.on(EventType.LOCALE_CHANGED, changeDayjsLocale)
    return () => eventBus.off(EventType.LOCALE_CHANGED, changeDayjsLocale)
  }, [])

  LanguageUtils.addListener(IListenerNameEnum.UpgradeBtnTextListener, () => {
    stores.userInfoSore.setUpgradeBtnText(stores.userInfoSore.data, t)
  })

  // 判断是否在 user-guide 页面
  const userGuideStr = "/user-guide"
  const isUserGuidePage = pathname.substring(0, userGuideStr.length) === userGuideStr
  const isOrder = pathname.substring(0, "/order".length) === "/order"

  const [searchParams] = useSearchParams()

  const shop = searchParams.get("shop") || ""
  const embedded = searchParams.get("embedded") || ""
  let authToken = searchParams.get("authToken") || ""

  if (authToken.length === 0) {
    try {
      const parcelpanelToken = window.sessionStorage.getItem("parcelpanel-token")
      const isValidToken = parcelpanelToken?.split(".").length === 3

      if (isValidToken) {
        authToken = parcelpanelToken
      }
    } catch (e) {
      console.error(e)
    }
  }


  const requestAuthLink = async () => {
    const urlSearchParams = new URLSearchParams(window.location.search)

    const postData = {}

    urlSearchParams.forEach((value, key) => {
      // @ts-ignore
      postData[key] = value
    })

    const { data: { shopify_oauth2: { redirect_url } } } = (await appApi.shopifyAuthLink(shop, postData)) || {}

    if (redirect_url) {
      window.location.href = redirect_url
    } else {
      console.error("shopify_oauth2.redirect_url is empty", postData)
    }
  }
  // 引入shopify货币转换js

  useScript("https://cdn.shopify.com/s/javascripts/currencies.js?time=" + new Date().getTime(), "currencies")

  // useErrorHandler()

  useEffect(() => {
    const { userInfoSore, upgradeStore } = stores

    if (import.meta.env.MODE !== "development" && embedded !== "1" && !isFromAdmin(authToken)) {
      console.log("满足条件，准备请求授权安装链接【embedded = 1】")
      // 须关闭 #loading-state：此分支不会走 userInfo().finally，否则遮罩永不消失（含 s-app-window iframe 缺 embedded 等场景）
      void requestAuthLink().finally(() => {
        window?.hideLoadingState()
      })
      return
    }

    try {
      initAPPBridge(searchParams, navigate)
    } catch (e) {
      console.error("initAPPBridge failed", e)
    }

    let cancelled = false

    appApi.userInfo()
      .then(({ data: response }: { data: IUserInfo }) => {
        if (cancelled) return

        if (response.code === APICode.OK) {

          const { link, update_scope } = get(response.data, "oauth") || { link: "", update_scope: false }

          if (update_scope && AppBridge.exist()) {
            AppBridge.redirect.dispatch(RedirectAction.REMOTE, { url: link })
            return
          }

          // 设置默认时区
          response.data?.timezone && setDefaultTimezone(response.data.timezone)

          userInfoSore.setData(response.data)
          userInfoSore.setIsLoadData(true)
          if (userInfoSore.isPPReturnBundle){
            upgradeStore.setAppType("bundle")
          }

          if (stores.userInfoSore.showBookDemoPopup){
            stores.Post.setPostTemplate(PostTemplate.EXPERT_HELP)
          }

          if (response.data) {
            const { data } = response

            try {
              const storeName = window.shopify ? window.shopify.config.shop : data?.store_name
              const params = {
                id: storeName,
                shop_owner: data?.user_name,
                email: data?.user_email,
                shop_name: data?.shop_name,
                shop_domain: storeName,
                shop_plan: data?.shop_plan_display_name, // 可选
                // shop_created_at: "店铺创建时间", // 可选
                app_name: "PP", // 应用名称，详见下方说明
                language: stores.commonStore.languageActiveCode, // 语言简码
                shopify_plan: data?.shop_plan_display_name, // Shopify套餐
                city: data?.city,
                country_name: data?.country_name,
              }

              // Customer.io脚本初始化
              if (window.cio && !window.isFromAdmin && isShopifyEmbedded()){
                window.cio.init(params)

                window.cio.page({
                  pathname: pathname, // 当前页面路径
                })
              }

              // CWReport脚本初始化
              // 超管后台拿不到 Shopify ID Token，中台 V1.1.0 起会拒绝其上报，且超管操作本就不该计入商家埋点，这里不初始化
              if (window.cwreport && !window.isFromAdmin){
                window.cwreport.init({
                  ...getCWReportBaseOptions(),
                  shopify_domain: storeName,
                  // 店铺首次安装时间，SDK 用于判断是否安装后 1 小时内的新用户
                  install_time: data?.installed_at ? String(data.installed_at) : undefined,

                  // 中台 V1.1.0 接口鉴权：上报请求携带 Shopify ID Token，经 bridge 校验后放行
                  get_shopify_id_token: getShopifyIdToken,
                })

                window.cwreport.identify(storeName, {
                  shopify_domain: storeName,
                })

                window.cwreport.initPublicProperty({
                  // 国家编码，示例：US
                  country_code: data?.country_name,
                  // 城市，示例：Fort Lauderdale
                  city: data?.city,
                  // shopify店铺类型，示例：Advanced
                  shopify_plan: data?.shop_plan_display_name,
                  // app名称，示例：CWILL Tracking
                  app_name: "CWILL Tracking",
                  // 传递语言语种，示例：en
                  language: stores.commonStore.languageActiveCode,
                })

                window.cwreport && window.cwreport.report("app_start")

                submitAdProperties()
              }

              // 创建主应用tracker实例
              const Tracker = createCrossSellTracker({
                apiKey: CROSS_SELL_APP_KEY.PARCEL_PANEL,
                environment: import.meta.env.MODE === "production" ? "production" : "test",
                enableLogging: true,
              })

              // 初始化主应用
              Tracker.initApp({
                shopify_id: Number(data.shop_id),
                user_id: Number(data.shop_id),
                domain: storeName,
                plan: data?.plan_name + `(${data?.plan_interval === AppPricingInterval.Every30_Days ? "Monthly" : "Annually" })`,
                shopify_version: data?.shop_plan_display_name,
                price: String(data.plan_price) || "0",
              }).then(() => {
                // 将tracker实例绑定到window对象，方便全局访问
                if (typeof window !== "undefined") {
                  window.CrossSellTracker = Tracker
                }
              })

              window.PB_ID_TOKEN = async () => {
                if (import.meta.env.DEV) {
                  return getDevToken({
                    app_id: APPEnum.Tracking,
                    myshopify_domain: storeName,
                  })
                }

                try {
                  const token = await TokenHelper.getToken()

                  console.log("PB_ID_TOKEN返回token:", token ? `${token.substring(0, 27)}...` : "空token")
                  return token
                } catch (error) {
                  console.error("PB_ID_TOKEN获取token失败:", error)
                  // 降级到本地authToken，需要加Bearer前缀
                  return authToken ? `Bearer ${authToken}` : ""
                }
              }
              window.PB_TOAST = toast
              window.PB_PAGE_LOADING = (loading: boolean) => {
                if (window.shopify) {
                  return window.shopify.loading(loading)
                }
                // return stores.commonStore.setPageLoading(loading)
              }

            } catch (e) {
              console.log(e)
            }


            if (data?.beta_features) {
              setBetaFeatures(data.beta_features)
            }

            if (data?.inject_js) {
              window?.parcelpanelInjectScript(`${env.base_api_url}${data.inject_js}`)
            }

            initIntercom(data, boot)

            // 白名单模式（超管后台 + 客户未授权）下所有推广 banner 都不展示，不必请求
            if (!stores.userInfoSore.isAdminScoped) {
              stores.commonStore.getBanner20493()
            }

            // 在项目中获取用户信息后设置sentry用户
            Sentry.setUser({
              id: data?.user_id, // 必填：用户唯一标识
              nickname: data?.shop_name,
            })
          }
        }
      })
      .finally(() => {
        window?.hideLoadingState()
      })

    return () => {
      cancelled = true
      shutdown()
    }
  }, [])

  useEffect(() => {
    const { globalMenuStore } = stores

    if (pathname !== "/" && globalMenuStore.selectedTabIndex === 0) {
      // let selectedTabIndex = _.map(globalMenuStore.tabs, 'id').indexOf(pathname);
      const selectedTabIndex = globalMenuStore.visibleTabs.findIndex(value => "/" === value.id ? value.id === pathname : pathname.startsWith(value.id))

      0 <= selectedTabIndex && globalMenuStore.selectTab(selectedTabIndex)
    }

    const locale = differenceLangMap[getI18n().language]
      ? differenceLangMap[getI18n().language]
      : getI18n()
        .language
        .toLocaleUpperCase()

    // 国家 / 时区只有 Integration 事件规则和 Tracking page 的 EDD 子页用得到，
    // 白名单模式下这两处都进不去，跳过这个请求
    if (!stores.userInfoSore.isAdminScoped) {
      trackingPageAPI.fetchCountriesAndTimeZones({ locale })
        .then(({ data }) => {
          stores.commonStore.setCountries(data.countries)
          stores.commonStore.setTimeZones(data.timeZones)
        })
    }
  }, [getI18n().language])

  useEffect(() => {
    // 如果首页, 第一次加载quickSetupData 用户引导步骤信息
    if (pathname === "/" && !stores.userInfoSore.quickSetupInitialized) {
      dashboard.getSetupGuide()
        .then(({ data }) => {
          stores.userInfoSore.setQuickSetupData(data.setup_guide)
        })
    }

    if (window.cio && window.isFromAdmin !== true && isShopifyEmbedded()) {
      window.cio.initialized && window.cio.page({
        pathname: pathname, // 当前页面路径
      })

      // 邮件模板编辑改用 Max Modal（EmailEditModalHost），不再走 App Bridge Fullscreen。
      // 左侧菜单选中态不再需要在这里同步：<ShopifyAppNav /> 会随 pathname 重渲染并声明 active。
    }
  }, [pathname])

  const prevPathnameRef = useRef(pathname)

  useLayoutEffect(() => {
    const { globalMenuStore } = stores

    pathnameHandle(pathname)

    const selectedTabIndex = globalMenuStore.visibleTabs.findIndex(value => "/" === value.id ? value.id === pathname : pathname.startsWith(value.id))

    0 <= selectedTabIndex && globalMenuStore.selectTab(selectedTabIndex)

    orderPost(pathname, navigate, searchParams)

    // 跳转后，返回页面顶部。
    // 但 /orders ↔ /orders/shipments/:id（侧边抽屉开/关/切换）属于同一个 Orders 布局内的切换，
    // 列表保持挂载，回顶会造成抖动，所以跳过。
    const isOrdersLayout = (p: string) => p === "/orders" || p.startsWith("/orders/shipments/")
    const stayInOrdersLayout = isOrdersLayout(prevPathnameRef.current) && isOrdersLayout(pathname)

    if (!stayInOrdersLayout) {
      window.scrollTo(0, 0)
    }
    prevPathnameRef.current = pathname

    // 通知管理后台，路径改变
    postRouteMessage(pathname, searchParams.toString())

  }, [pathname])

  const isThemeLayoutAppWindowContentRoute = pathname === "/tracking-page/customizeBackdrop"
    || pathname === "/tracking-page/customizeShowcase"

  // 是否显示全局菜单栏
  const showTopTab = !isUserGuidePage
    && !startsWith(pathname, "/orders/update-status")
    && !startsWith(pathname, "/get-help")
    && !isThemeLayoutAppWindowContentRoute

  const isEmailEditPage = startsWith(pathname, "/settings/email-edit")
  const isAnalyticsPage = startsWith(pathname, "/analytics")
  const isBillingPage = startsWith(pathname, "/billing")
  const hideUpgradeRequired = pathname === "/billing/plans" || pathname === "/billing" // || true

  // Tracking page 根路由 / CSS&HTML 的页面元素：商家全量路由和超管后台白名单共用同一份定义
  const trackingPageRootElement = (
    <DynamicLoadError>
      <Suspense fallback={<SkeletonTrackingPageAppearance />}>
        <DisplayOptions trackingPageStore={stores.trackingPageStore} />
      </Suspense>
    </DynamicLoadError>
  )
  const trackingPageAppearanceElement = (
    <DynamicLoadError>
      <Suspense fallback={<SkeletonTrackingPage />}>
        <DisplayOptions trackingPageStore={stores.trackingPageStore} />
      </Suspense>
    </DynamicLoadError>
  )
  const trackingPageCSSHTMLElement = (
    <DynamicLoadError>
      <Suspense fallback={<SkeletonTrackingPage />}>
        <CustomCSSAndHTML />
      </Suspense>
    </DynamicLoadError>
  )

  /**
   * 订单模块路由：商家全量路由和超管后台白名单共用同一份定义
   * scoped 为 true 时，子路由逐个过 ADMIN_SCOPED_ROUTES 的白名单（excludePaths 里的不注册）
   */
  const renderOrdersRoutes = (scoped = false) => {
    const allow = (path: string) => !scoped || isAdminScopedPath(path)

    return (
      <Route path="/orders">
        <Route element={(
          <DynamicLoadError>
            <Suspense fallback={<SkeletonOrders />}>
              <Orders />
              {!scoped && (
                <FooterHelp>
                  <Footer pathname={pathname} />
                </FooterHelp>
              )}
              <Outlet />
            </Suspense>
          </DynamicLoadError>
        )}
        >
          <Route index element={null} />
          {allow("/orders/shipments") && <Route path="shipments/:shipmentID" element={<ShipmentDrawer />} />}
        </Route>
        {allow("/orders/update-status") && <Route path="update-status" element={<OrdersUpdateStatus />} />}
        {allow("/orders/detail") && <Route path="detail" element={<OrdersDetail />} />}
        {allow("/orders/carrier-matching") && <Route path={"carrier-matching"} element={<CarrierMatching />} />}
      </Route>
    )
  }

  /**
   * 超管后台（非 Shopify admin app）代客进入、且客户未授权时的路由表
   *
   * 只注册 ADMIN_SCOPED_ROUTES 白名单里的页面，其余一律重定向到 ADMIN_SCOPED_FALLBACK_PATH。
   * 新增白名单页面：先在 utils/customerAuthGate 的 ADMIN_SCOPED_ROUTES 加配置（菜单会自动跟着收窄），
   * 再在这里补一段用 isAdminScopedPath() 包起来的 <Route>。
   */
  const renderAdminScopedRoutes = () => {
    const showTrackingPageRoot = isAdminScopedPath("/tracking-page")
    const showTrackingPageAppearance = isAdminScopedPath("/tracking-page/appearance")
    const showTrackingPageCSSHTML = isAdminScopedPath("/tracking-page/CSS&HTML")

    return (
      <Routes>
        {/* 首页（Dashboard）不在白名单里，直接重定向到 Orders */}
        <Route path="/" element={<Navigate to={ADMIN_SCOPED_FALLBACK_PATH} replace />} />

        {isAdminScopedPath("/orders") && renderOrdersRoutes(true)}

        {(showTrackingPageRoot || showTrackingPageAppearance || showTrackingPageCSSHTML) && (
          <Route path="/tracking-page" element={<TrackingPage />}>
            {showTrackingPageRoot && <Route path="" element={trackingPageRootElement} />}
            {showTrackingPageAppearance && <Route path="appearance" element={trackingPageAppearanceElement} />}
            {showTrackingPageCSSHTML && <Route path="CSS&HTML" element={trackingPageCSSHTMLElement} />}
          </Route>
        )}

        <Route path="*" element={<Navigate to={ADMIN_SCOPED_FALLBACK_PATH} replace />} />
      </Routes>
    )
  }

  return <Observer>{() => (
    <>
      {
        !hideUpgradeRequired && stores.userInfoSore.isLoadData &&
        <DynamicLoadError>
          <Suspense fallback={null}>
            <UpgradeRequired
              active={stores.upgradeStore.upgradeModalActive}
              handleChange={stores.upgradeStore.handleCloseModal}
              btnText={stores.upgradeStore.btnText}
            />
          </Suspense>
        </DynamicLoadError>
      }

      {/* 中台的组件Provider*/}
      <CWComponentsProvider />

      {/* Shopify admin 左侧应用菜单（声明式，App Bridge 读 DOM 后同步给 admin） */}
      <ShopifyAppNav />

      {/* todo 改了字体大小导致的tab项会换行？ */}

      {stores.userInfoSore.isLoadData && showTopTab && !isEmailEditPage && <div id="global-menu">
        <div className="flex-1">
          <HeaderTab />
        </div>
      </div>}

      <PostLayer />

      {/* 用户基础数据就绪后再加载页面；s-app-window 子路由需提前挂载以尽快渲染 s-page 顶栏标题，否则在 iframe 内会先显示默认应用名 */}
      {stores.userInfoSore.isLoadData || isThemeLayoutAppWindowContentRoute ? (
        // 超管后台代客进入且客户未授权：走白名单路由；其余情况（商家环境、已授权）保持全量路由不变
        stores.userInfoSore.isAdminScoped ? renderAdminScopedRoutes() : (
          <Routes>
            <Route path="/">
              <Route path="" element={<Dashboard />} />
              <Route path="recommend-apps" element={<RecommendPage />} />
            </Route>
            <Route path="/tracking-page" element={<TrackingPage />}>

              <Route path="" element={trackingPageRootElement} />
              <Route path="appearance" element={trackingPageAppearanceElement} />
              <Route path="languages" element={(
                <DynamicLoadError>
                  <Suspense fallback={<SkeletonTrackingPage />}>
                    <TrackingPageTranslations />
                  </Suspense>
                </DynamicLoadError>
              )}
              />
              <Route path="customShipmentStatus" element={(
                <DynamicLoadError>
                  <Suspense fallback={<SkeletonTrackingPage />}>
                    <CustomOrderStatus trackingPageStore={stores.trackingPageStore} />
                  </Suspense>
                </DynamicLoadError>
              )}
              />
              <Route path="estimatedDeliveryTime" element={(
                <DynamicLoadError>
                  <Suspense fallback={<SkeletonTrackingPage />}>
                    <EstimatedDeliveryTime />
                  </Suspense>
                </DynamicLoadError>
              )}
              />
              <Route path="productRecommendation" element={(
                <DynamicLoadError>
                  <Suspense fallback={<SkeletonTrackingPage />}>
                    <ProductRecommendationUpsell trackingPageStore={stores.trackingPageStore} />
                  </Suspense>
                </DynamicLoadError>
              )}
              />
              <Route path="seoOptimization" element={(
                <DynamicLoadError>
                  <Suspense fallback={<SkeletonTrackingPage />}>
                    <SEOOptimization />
                  </Suspense>
                </DynamicLoadError>
              )}
              />
              <Route path="trackingPageUrl" element={(
                <DynamicLoadError>
                  <Suspense fallback={<SkeletonTrackingPage />}>
                    <TrackingPageURL />
                  </Suspense>
                </DynamicLoadError>
              )}
              />
              <Route path="CSS&HTML" element={trackingPageCSSHTMLElement} />
              <Route path="customizeBackdrop" element={(
                <DynamicLoadError>
                  <CustomizeBackdropPage />
                </DynamicLoadError>
              )}
              />
              <Route path="customizeShowcase" element={(
                <DynamicLoadError>
                  <CustomizeShowcasePage />
                </DynamicLoadError>
              )}
              />
            </Route>
            {renderOrdersRoutes()}
            <Route path="/settings/" element={<Settings />}>
              <Route path="" element={(
                <DynamicLoadError>
                  <Suspense fallback={<SkeletonGeneral />}>
                    <General />
                  </Suspense>
                </DynamicLoadError>
              )}
              />
              <Route path="general" element={(
                <DynamicLoadError>
                  <Suspense fallback={<SkeletonGeneral />}>
                    <General />
                  </Suspense>
                </DynamicLoadError>
              )}
              />
              <Route path="shipping-notifications" element={(
                <DynamicLoadError>
                  <Suspense fallback={<SkeletonNotifications />}>
                    <ShippingNotifications />
                  </Suspense>
                </DynamicLoadError>
              )}
              />
              <Route path="pre_purchase_edd" element={(
                <DynamicLoadError>
                  <Suspense fallback={<SkeletonPrePurchaseEDD />}>
                    <PrePurchaseEDD />
                  </Suspense>
                </DynamicLoadError>
              )}
              />
              <Route path="system-notifications" element={(
                <DynamicLoadError>
                  <Suspense fallback={<SkeletonNotifications />}>
                    <SystemNotifications />
                  </Suspense>
                </DynamicLoadError>
              )}
              />
            </Route>
            <Route path="/settings/email-edit/:orderStatus" element={(
              <DynamicLoadError>
                <Suspense fallback={<FullScreenLoading />}>
                  <EmailEditor />
                </Suspense>
              </DynamicLoadError>
            )}
            />

            <Route path="/billing" element={<DynamicLoadError>
              <Suspense fallback={null}>
                <Billing />
                <FooterHelp>
                  <Footer pathname={pathname} />
                </FooterHelp>
              </Suspense>
            </DynamicLoadError>}
            />
            <Route path="/billing/plans" element={<Account />} />
            {/*
            套餐页（Account 组件）归到 /billing 子路径下，左侧菜单才能靠 URL 层级点亮 Billing
            （admin 的高亮只看菜单项 url，见 ShopifyAppNav/resolveActiveTabId.ts）。
            旧地址 /account 仍被后端 return_url 和站外链接使用，这里带 query 一起重定向过去。
          */}
            <Route path="/account" element={<Navigate to={{ pathname: "/billing/plans", search }} replace />} />
            <Route path="/integration" element={
              <DynamicLoadError>
                <Suspense fallback={<SkeletonIntegration />}>
                  <Integration />
                </Suspense>
              </DynamicLoadError>
            }
            />
            <Route path="/integration/events" element={
              <DynamicLoadError>
                <Suspense fallback={<SkeletonIntegration />}>
                  <IntegrationEvents />
                </Suspense>
              </DynamicLoadError>
            }
            />

            <Route path="/analytics/" element={<Analytics />}>
              <Route path="" element={(
                <DynamicLoadError>
                  <Suspense fallback={<SkeletonAnalytics />}>
                    <AnalyticsShipment />
                  </Suspense>
                </DynamicLoadError>)}
              />
              <Route path="shipment" element={(
                <DynamicLoadError>
                  <Suspense fallback={<SkeletonAnalytics />}>
                    <AnalyticsShipment />
                  </Suspense>
                </DynamicLoadError>)}
              />
              <Route path="transit-time" element={(
                <DynamicLoadError>
                  <Suspense fallback={<SkeletonAnalytics />}>
                    <AnalyticsTransitTime />
                  </Suspense>
                </DynamicLoadError>)}
              />
              <Route path="tracking-page" element={(
                <DynamicLoadError>
                  <Suspense fallback={<SkeletonAnalytics />}>
                    <AnalyticsTrackingPage />
                  </Suspense>
                </DynamicLoadError>)}
              />
              <Route path="delivery-time" element={(
                <DynamicLoadError>
                  <Suspense fallback={<SkeletonAnalytics />}>
                    <AnalyticsDeliveryTime />
                  </Suspense>
                </DynamicLoadError>)}
              />
              <Route path="exceptions" element={(
                <DynamicLoadError>
                  <Suspense fallback={<SkeletonAnalytics />}>
                    <AnalyticsExceptions />
                  </Suspense>
                </DynamicLoadError>)}
              />
              <Route path="shipping-notifications" element={(
                <DynamicLoadError>
                  <Suspense fallback={<SkeletonAnalytics />}>
                    <AnalyticsShippingNotifications />
                  </Suspense>
                </DynamicLoadError>)}
              />
            </Route>

            <Route path="/protection/" element={<DeliveryGuarantee />}>
              <Route path="" element={(
                <DynamicLoadError>
                  <Suspense fallback={<SkeletonDelivery />}>
                    <DGSetting />
                  </Suspense>
                </DynamicLoadError>)}
              />
              <Route path="general" element={(
                <DynamicLoadError>
                  <Suspense fallback={<SkeletonDelivery />}>
                    <DGSetting />
                  </Suspense>
                </DynamicLoadError>)}
              />
              <Route path="covers" element={(
                <DynamicLoadError>
                  <Suspense fallback={<SkeletonTable />}>
                    <Covers />
                  </Suspense>
                </DynamicLoadError>)}
              />
              <Route path="claims" element={(
                <DynamicLoadError>
                  <Suspense fallback={<SkeletonTable />}>
                    <Claims />
                  </Suspense>
                </DynamicLoadError>)}
              />
              <Route path="bills/:status?" element={(
                <DynamicLoadError>
                  <Suspense fallback={<SkeletonTable />}>
                    <Transactions />
                  </Suspense>
                </DynamicLoadError>
              )}
              />
            </Route>

            <Route path="/add-ons" element={<AddOns />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        )
      ) : (
        <FullScreenLoading />
      )}

      {/* 白名单模式（超管后台 + 客户未授权）下不展示 FooterHelp */}
      {stores.userInfoSore.isLoadData && !stores.userInfoSore.isAdminScoped && !isEmailEditPage && !isUserGuidePage && !isOrder && !isAnalyticsPage && !isBillingPage && !isThemeLayoutAppWindowContentRoute &&
      <FooterHelp>
        <Footer pathname={pathname} />
      </FooterHelp>}

      {/* 广告弹框 */}
      {/* {!window.isFromAdmin && stores.userInfoSore.isLoadData && !stores.userInfoSore.data.showNewUserGuide && */}
      {/* <MarketingPopup />} */}

      {/* 开发环境：客户授权管控模拟开关（生产构建不渲染） */}
      <DevCustomerAuthGateSwitch />

      <FeedbackModal
        rate={stores.Post.rate}
        show={stores.Post.showCommentModal && stores.Post.rate < 5}
        onClose={() => stores.Post.setShowCommentModal(false)}
      />
    </>
  )}</Observer>
}

function HeaderTab() {
  const navigate = useNavigate()

  // 难道不能这解构，要在 Observer内解构？
  const { globalMenuStore } = stores
  const { t } = useTranslation("menu")

  useEffect(() => {
    globalMenuStore.setTabs(globalMenuStore.tabs.map(value => {
      value.content = t(value.i18nKey)
      return value
    }))
  }, [getI18n().language])

  return (
    <Observer>{() => isShopifyEmbedded() && !window.isFromAdmin ? null : (
      <Tabs
        tabs={globalMenuStore.visibleTabs}
        selected={globalMenuStore.selectedTabIndex}
        onSelect={(id) => navigate(globalMenuStore.visibleTabs[id]?.id || "/")}
      />
    )}</Observer>
  )
}

function initAPPBridge(searchParams: URLSearchParams, navigate: NavigateFunction) {
  // Shopify 重定向过来，携带了这些参数
  const shop = searchParams.get("shop") as string
  const host = searchParams.get("host") as string
  // 从我们后台访问，会携带这个东西
  let authToken = searchParams.get("authToken") || ""

  if (authToken.length === 0) {
    try {
      const parcelpanelToken = window.sessionStorage.getItem("parcelpanel-token")
      const isValidToken = parcelpanelToken?.split(".").length === 3

      if (isValidToken) {
        authToken = parcelpanelToken
      }
    } catch (e) {
      console.error(e)
    }
  }

  // 如果存在这个东西表示是从我们超管后台进来的，不执行跳转
  if (isFromAdmin(authToken)) {
    console.log(`%c-->超管后台环境, isMobile: ${isMobile() ? "true" : "false"} , isShopifyEmbedded: ${isShopifyEmbedded() ? "true" : "false"}`, "background-color:#4B51F1; color: white; padding: 4px 8px;")
    // 添加这个是为了让父页面能够判断出来
    window.isFromAdmin = true
    stores.commonStore.setAuthToken(authToken)
    return
  }

  if (import.meta.env.MODE === "development") {
    console.log("%c开发环境", "background-color:#00a0ac; color: white; padding: 4px 8px;")
    if (!isShopifyEmbedded()){
      return
    }
  }

  if (shop && shop.length > 5 && host && host.length > 5 && isShopifyEmbedded()) {
    if (isMobile()) {
      console.log("%c APP 运行环境：mobile", "background-color:#007557; color: white; padding: 4px 8px;")
    } else {
      console.log("%c APP 运行环境：desktop web browser", "background-color:#007557; color: white; padding: 4px 8px;")
    }
  } else {
    console.log("%c app is not running as an embedded app", "background-color:red; color: white; padding: 4px 8px;")
  }

  AppBridge.set(host)
    .subscribe(Redirect.Action.APP, (data) => {
      const path: string = data?.path ?? getAppLink()

      if (path === "/" || path === getAppLink("/") || startsWith(path, getAppLink("?"))) {
        setCurrentShopifyAppPath("/")
        navigate("/")
        return
      }

      if (startsWith(path, getAppBase())) {
        navigate(setCurrentShopifyAppPath(
          path.replace(getAppBase(), ""),
        ))
      }
    })
}

/** 初始化 Intercom */
function initIntercom(data: UserInfoResponseData, boot: (props?: IntercomProps) => void) {
  try {
    if (UrlUtils.parseBoolean(UrlUtils.get("hidden_intercom")) || import.meta.env.VITE_SHOW_INTERCOM === "false") {
      return
    }

    if (window.ParcelWILL && window.ParcelWILL?.isFromAdmin && window.ParcelWILL.hiddenIntercom) {
      return
    }

    window.parent.window.PPRemoveHiddenIntercomFromUrl?.()
  } catch (e) {
    console.debug(e)
  }

  // 2026-07-10 改回 ParcelPanel
  // 因为 Intercom 严重依赖这个名字做各种流程判断。
  // 但后续务必和 Intercom 相关负责人沟通好再迁移至 ParcelWILL 这个名字
  let from_app = "ParcelPanel"
  let user_id = data?.user_id

  try {
    const intercomUidPrefix = import.meta.env.VITE_INTERCOM_UID_PREFIX || ""
    const intercomFromAppPrefix = import.meta.env.VITE_INTERCOM_FROM_APP_PREFIX || ""

    if (intercomUidPrefix.includes("test_")) {
      user_id = `${import.meta.env.VITE_INTERCOM_UID_PREFIX}${user_id}`
    }

    if (intercomFromAppPrefix.includes("Test_")) {
      from_app = `${import.meta.env.VITE_INTERCOM_FROM_APP_PREFIX}${from_app}`
    }
  } catch (e) {
    console.error(e)
  }

  boot(getIntercomBootProps(data, user_id, from_app))
}

export default App
